/* Copyright (c) 2017 Backpack Health LLC */

import * as _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { TextLink } from './TextLink';
import { Button } from './buttons/Buttons';
import { Caption, Small } from './Text';
import DropdownList from './DropdownList';
import IconClose from './icons/IconClose';

import { Grid, Cell } from './Grid';
import DateUtil from '../../utilities/DateUtil';

class DatePicker extends React.Component {

  constructor(props) {
    super(props);

    this.handleOnChangeMonth = this.handleOnChangeMonth.bind(this);
    this.handleOnChangeDay = this.handleOnChangeDay.bind(this);
    this.handleOnChangeYear = this.handleOnChangeYear.bind(this);
    this.handleDone = this.handleDone.bind(this);
    this.handleDoNotRemember = this.handleDoNotRemember.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.setToday = this.setToday.bind(this);

    const date = props.date ? props.date : props.defaultDate;
    const precision = props.precision ? props.precision : props.defaultPrecision;
    const dateData = DateUtil.breakIntoParts(date, precision);

    this.state = {
      month: dateData.month,
      day: dateData.day,
      year: dateData.year || new Date().getFullYear(),
      monthOptions: DateUtil.getMonthOptions(props.isFuzzy),
      yearOptions: DateUtil.getYearOptions(props.earliestDateLimit, props.latestDateLimit),
      dayOptions: DateUtil.getDayOptions(dateData.year, dateData.month, props.isFuzzy),
    };
  }

  setToday() {
    const todayParts = DateUtil.breakIntoParts(DateUtil.today(), 'P1D');

    this.setState({
      year: todayParts.year,
      month: todayParts.month,
      day: todayParts.day,
    });
  }

  handleOnChangeMonth(event) {
    let month = parseInt(event.target.value, 10);
    month = _.isNaN(month) ? '' : month;
    this.changeDate(this.state.year, month, this.state.day, 'month');
  }

  handleOnChangeDay(event) {
    let day = parseInt(event.target.value, 10);
    day = _.isNaN(day) ? '' : day;
    this.changeDate(this.state.year, this.state.month, day, 'day');
  }

  handleDoNotRemember() {
    this.setState({
      month: '',
      day: '',
    });
    this.props.onDateSubmit({
      date: '',
      precision: '',
    });
  }

  handleOnChangeYear(event) {
    let year = parseInt(event.target.value, 10);
    year = _.isNaN(year) ? '' : year;
    this.changeDate(year, this.state.month, this.state.day, 'year');
  }

  changeDate(year, month, day, unitChanged) {
    const dateData = DateUtil.insureValidDateData(year, month, day, unitChanged, this.props.earliestDateLimit, this.props.latestDateLimit);
    const dayOptions = DateUtil.getDayOptions(dateData.year, dateData.month, this.props.isFuzzy);

    this.setState({
      year: dateData.year,
      month: dateData.month,
      day: dateData.day,
      dayOptions,
    });
  }

  isDateFuzzy() {
    return !this.state.year || !this.state.month || !this.state.day;
  }

  handleDone(event) {
    event.preventDefault();

    let internalDate = DateUtil.convertPartsToInternalFormat(this.state.year, this.state.month, this.state.day);

    if (this.isDateFuzzy() && !this.props.isFuzzy) {
      internalDate = { date: '', precision: '' };
    }

    if (!this.props.allowNull && !internalDate.date) {
      return;
    }

    this.props.onDateSubmit({
      date: internalDate.date,
      precision: internalDate.precision,
    });
  }

  handleClose() {
    this.props.onDateSubmit();
  }

  render() {
    return (
      <div className="cmp-date-picker">
        <div className="cmp-date-picker__content">
          <Caption noBottomMargin color="neutral">
            <FormattedMessage id={this.props.label} />
          </Caption>
          <div className="cmp-date-picker__close" onClick={this.handleClose}>
            <IconClose.Small />
          </div>

          <div className="cmp-date-picker__inputs">
            <div className="cmp-date-picker__month">
              <DropdownList
                label={<FormattedMessage id="fuzzydate.month.field.label" />}
                value={this.state.month}
                onChange={this.handleOnChangeMonth}
                values={this.state.monthOptions}
              />
            </div>
            <div className="cmp-date-picker__day">
              <DropdownList
                label={<FormattedMessage id="fuzzydate.day.field.label" />}
                value={this.state.day}
                onChange={this.handleOnChangeDay}
                values={this.state.dayOptions}
              />
            </div>
            <div className="cmp-date-picker__year">
              <DropdownList
                label={<FormattedMessage id="fuzzydate.year.field.label" />}
                value={this.state.year}
                onChange={this.handleOnChangeYear}
                values={this.state.yearOptions}
              />
            </div>
          </div>
        </div>
        <div className="cmp-date-picker__finished">
          <Grid justifyContent="space-between" alignItems="center">
            {
              this.props.allowNull ? (
                <Cell minWidth={60}>
                  <TextLink onClick={this.handleDoNotRemember}>
                    <Small noBottomMargin>
                      <FormattedMessage id="fuzzydate.unsure.button.label" />
                    </Small>
                  </TextLink>
                </Cell>
              ) : <div />
            }
            {
              this.props.hasToday ? (
                <Cell minWidth={60}>
                  <TextLink onClick={this.setToday}>
                    <Small noBottomMargin>
                      <FormattedMessage id="fuzzydate.today.button.label" />
                    </Small>
                  </TextLink>
                </Cell>
              ) : <div />
            }
              <div>
                <Button onClick={this.handleDone} type="primary">
                  <FormattedMessage id="fuzzydate.submit.button.label" />
                </Button>
              </div>
          </Grid>
        </div>
      </div>
    );
  }
}
DatePicker.propTypes = {
  label: PropTypes.any,
  date: PropTypes.string,
  precision: PropTypes.string.isRequired,
  onDateSubmit: PropTypes.func.isRequired,
  earliestDateLimit: PropTypes.string,
  latestDateLimit: PropTypes.string,
  defaultDate: PropTypes.string,
  defaultPrecision: PropTypes.string,
  isFuzzy: PropTypes.bool,
  hasToday: PropTypes.bool,
  allowNull: PropTypes.bool,
};
DatePicker.defaultProps = {
  defaultDate: '',
  defaultPrecision: 'P1Y',
  earliestDateLimit: `${new Date().getFullYear() - 200}-01-01`,
  latestDateLimit: new Date().toISOString().split('T')[0],
};

export default DatePicker;
